import React, {
  type ChangeEvent,
  type ComponentPropsWithoutRef,
  type ComponentType,
  type ForwardedRef,
  type KeyboardEvent,
  useCallback,
  useMemo,
  useRef,
} from 'react';

import { genericForwardRef } from '../../../helpers/genericForwardRef';
import { isKey } from '../../../helpers/keyboard';
import {
  useClickAway,
  useMaybeControlledValue,
  useRefAndForward,
  useValidityState,
  type ValidityChangeCallback,
} from '../../../hooks';
import { DownFilledIcon } from '../../Icon/icons';
import { Overlay } from '../../Overlay';
import { Control, type ControlProps } from '../Control';

import { type SelectableItem, type SelectContext, type SelectOptionRegistration, SelectProvider } from './context';
import { keepOptionVisible } from './keepOptionVisible';
import { Styled } from './styles';
import { useSearch } from './useSearch';

type ControlPropsToForward = Omit<ComponentPropsWithoutRef<'div'>, 'ref' | keyof ControlProps> & ControlProps;
type OverlayProps = ComponentPropsWithoutRef<typeof Overlay>;
type OverlayPropsToForward = {
  children?: OverlayProps['children'];
  overlayMaxHeight?: OverlayProps['maxHeight'];
};
type InputPropsToForward = Pick<ComponentPropsWithoutRef<'input'>, 'required' | 'name' | 'form'>;

export interface SelectOwnProps<T extends SelectableItem> {
  placeholder?: string;
  radio?: boolean;
  controlRef?: ForwardedRef<HTMLElement | null>;
  search?: string | null;
  onSearchChange?: (search: string | null) => void;
  isOpened?: boolean;
  onOpenedChange?: (isOpened: boolean) => void;
  optionsWrapperId?: string;
  onValidityChange?: ValidityChangeCallback;
  value: T | null;
  disableKeyboardArrowSelection?: boolean;
  /** For options fetched after the search changes: the list still shows the previous answer when Enter is pressed. */
  disableEnterSelection?: boolean;
  onChange: (value: T) => void;
  formatLabel?: (value: T | null) => string;
  onFocus?: (e: FocusEvent) => void;
  disablePortalForOptions?: boolean;
}

export type SelectControlProps<T extends SelectableItem> = Omit<
  OverlayPropsToForward & ControlPropsToForward & InputPropsToForward,
  keyof SelectOwnProps<T>
> &
  SelectOwnProps<T>;

const defaultSuffix = <DownFilledIcon size="m" />;

const SelectControlImpl = <T extends SelectableItem>(
  {
    controlRef = null,
    radio,
    onChange,
    value,
    search,
    isOpened: maybeIsOpened,
    onOpenedChange: maybeOnOpenedChange,
    optionsWrapperId,
    onSearchChange,
    formatLabel = String,
    overlayMaxHeight,
    children,
    placeholder,
    required = false,
    suffix = defaultSuffix,
    onValidityChange,
    onFocus,
    disableKeyboardArrowSelection,
    disableEnterSelection,
    disablePortalForOptions,
    ...controlProps
  }: SelectControlProps<T>,
  ref: ForwardedRef<HTMLDivElement>,
) => {
  const [isOpened, onOpenedChange] = useMaybeControlledValue({
    valueFromProps: maybeIsOpened,
    onChangeFromProps: maybeOnOpenedChange,
    defaultValue: false,
  });
  const isSearchable = !!onSearchChange && search !== undefined;
  const overlayRef = useRefAndForward<HTMLDivElement | null>(null, ref);
  const controlLocalRef = useRefAndForward<HTMLElement | null>(null, controlRef);

  const optionRegistry = useRef(new Map<object, SelectOptionRegistration<T>>());

  /* Read only from event handlers, so a ref is enough — registering must not re-render the control. */
  const registerOption = useCallback((key: object, registration: SelectOptionRegistration<T>) => {
    optionRegistry.current.set(key, registration);

    return () => {
      optionRegistry.current.delete(key);
    };
  }, []);

  const context: SelectContext<T> = useMemo(
    () => ({
      formatLabel: (optionValue: T | null) => (optionValue ? formatLabel(optionValue) : ''),
      onChange: (nextValue: T) => {
        onOpenedChange(false);
        onChange(nextValue);
      },
      value,
      variant: radio ? 'radio' : 'select',
      values: null,
      registerOption,
    }),
    [radio, onChange, onOpenedChange, value, formatLabel, registerOption],
  );

  const labelFromValue = context.formatLabel(value);
  const input = useSearch({ labelFromValue, onSearchChange, search, placeholder, isOpened });

  useClickAway({
    containerRefs: [controlLocalRef, overlayRef],
    listener: useCallback(() => {
      if (isOpened) onOpenedChange(false);
    }, [isOpened, onOpenedChange]),
  });

  useValidityState({
    onValidityChange,
    getState: useCallback(() => ({ valueMissing: required && value === null }), [required, value]),
  });

  const commonControlProps: Partial<ComponentPropsWithoutRef<typeof Control>> = {
    ref: controlLocalRef,
    role: 'listbox',
    suffix,
    onFocus: (e: FocusEvent) => {
      if (controlProps.disabled) return;
      onOpenedChange(true);
      if (onFocus) {
        onFocus(e);
      }
    },
    style: {
      pointerEvents: !controlProps.disabled ? 'auto' : 'none',
      cursor: !controlProps.disabled ? 'pointer' : 'not-allowed',
      minHeight: '2.625rem',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
    },
    'aria-multiselectable': false,
    'aria-required': required,
  };

  /*
   * Document order, not mount order: options may be registered by a wrapper component, and the
   * overlay can be portalled. Scanning the document is safe because only this control's own
   * options are in the registry — another select's options are filtered out.
   */
  const getOrderedOptions = useCallback(() => {
    const registrationByNode = new Map<Element, SelectOptionRegistration<T>>();
    optionRegistry.current.forEach(registration => {
      const node = registration.getNode();
      if (node) {
        registrationByNode.set(node, registration);
      }
    });

    if (!registrationByNode.size) {
      return [];
    }

    return [...document.querySelectorAll<HTMLElement>('[role="option"]')].flatMap(node => {
      const registration = registrationByNode.get(node);
      return registration ? [{ node, value: registration.value, isPlaceholder: registration.isPlaceholder }] : [];
    });
  }, []);

  const selectOption = useCallback(
    (direction: 'up' | 'bottom'): void => {
      const orderedOptions = getOrderedOptions();
      if (!orderedOptions.length) {
        return;
      }

      /* The null-safe labels Option highlights with: an empty value never reaches the consumer's formatLabel. */
      const selectedLabel = context.formatLabel(value);
      const selectedKey = orderedOptions.findIndex(option => context.formatLabel(option.value) === selectedLabel);
      const nextOption = orderedOptions[direction === 'up' ? selectedKey - 1 : selectedKey + 1];

      if (nextOption) {
        keepOptionVisible(nextOption.node);
        onChange(nextOption.value);
      }
    },
    [context, getOrderedOptions, onChange, value],
  );

  const selectValue = useCallback(
    (event: KeyboardEvent): void => {
      if (isOpened && !controlProps.disabled && !disableEnterSelection) {
        /* An empty string is the "-" entry that clears a filter: never the option a search leaves. */
        const [onlyOption, ...otherOptions] = getOrderedOptions().filter(
          option => option.value !== '' && !option.isPlaceholder,
        );
        if (onlyOption && !otherOptions.length) {
          onChange(onlyOption.value);
        }
      }
      onOpenedChange(false);
      const target = event.target as HTMLInputElement;
      target.blur();
    },
    [controlProps.disabled, disableEnterSelection, getOrderedOptions, isOpened, onChange, onOpenedChange],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent): void => {
      if (isKey('enter')(event)) {
        selectValue(event);
      }
      if (disableKeyboardArrowSelection) {
        return;
      }
      /* The select owns the arrows once navigation is on — otherwise the browser scrolls the page too. */
      if (isKey('up')(event) || isKey('ArrowUp')(event)) {
        event.preventDefault();
        selectOption('up');
      }
      if (isKey('bottom')(event) || isKey('ArrowDown')(event)) {
        event.preventDefault();
        selectOption('bottom');
      }
    },
    [disableKeyboardArrowSelection, selectOption, selectValue],
  );

  return (
    <SelectProvider value={context}>
      {isSearchable ? (
        <Control
          as="input"
          placeholder={input.placeholder}
          value={input.value}
          onChange={(event: ChangeEvent<HTMLInputElement>) => onSearchChange?.(event.target.value)}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          {...controlProps}
          {...commonControlProps}
        />
      ) : (
        <Control onKeyDown={handleKeyDown} {...controlProps} {...commonControlProps} tabIndex={0}>
          {value && context.formatLabel(value)}
          {!value && placeholder && <Styled.Placeholder>{placeholder}</Styled.Placeholder>}
        </Control>
      )}

      <Overlay
        variant="fit-target"
        ref={overlayRef}
        targetRef={controlLocalRef}
        isOpened={isOpened && React.Children.count(children) > 0}
        maxHeight={overlayMaxHeight}
        disablePortalForOptions={disablePortalForOptions}
        id={optionsWrapperId}
      >
        {children}
      </Overlay>
    </SelectProvider>
  );
};

export const SelectControl = genericForwardRef(SelectControlImpl);
(SelectControl as ComponentType).displayName = 'Select.Control';
